import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import { vehicleService, VehicleCategoryDTO } from '../../../../services/vehicleService';
import { ArrowLeft, Car, AlertCircle } from 'lucide-react';
import '../CarRentalPage.css';

interface FormData {
  vehicleNumber: string;
  registrationNumber: string;
  vin: string;
  make: string;
  model: string;
  year: number;
  vehicleCategoryId: string;
  color: string;
  seatingCapacity: number;
  fuelType: string;
  transmission: string;
  mileage: number;
  rentalRate: number;
  securityDeposit: number;
  gpsAvailable: boolean;
  insuranceDetails: string;
  status: string;
  isActive: boolean;
}

const EMPTY: FormData = {
  vehicleNumber: '', registrationNumber: '', vin: '', make: '', model: '', year: new Date().getFullYear(),
  vehicleCategoryId: '', color: '', seatingCapacity: 4, fuelType: 'Petrol', transmission: 'Manual',
  mileage: 0, rentalRate: 0, securityDeposit: 0, gpsAvailable: false, insuranceDetails: '',
  status: 'Available', isActive: true,
};

const VehicleCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [categories, setCategories] = useState<VehicleCategoryDTO[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [formData, setFormData] = useState<FormData>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    vehicleService.getCategories().then((res: any) => setCategories(res.data?.data ?? []));
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      setLoadingData(true);
      vehicleService.getById(Number(id))
        .then((res: any) => {
          const d = res.data?.data ?? res.data;
          setFormData({
            vehicleNumber: d.vehicleNumber, registrationNumber: d.registrationNumber, vin: d.vin ?? '',
            make: d.make, model: d.model, year: d.year, vehicleCategoryId: String(d.vehicleCategoryId),
            color: d.color ?? '', seatingCapacity: d.seatingCapacity, fuelType: d.fuelType,
            transmission: d.transmission, mileage: d.mileage, rentalRate: d.rentalRate,
            securityDeposit: d.securityDeposit, gpsAvailable: d.gpsAvailable,
            insuranceDetails: d.insuranceDetails ?? '', status: d.status, isActive: d.isActive,
          });
        })
        .catch(() => setError('Failed to load vehicle'))
        .finally(() => setLoadingData(false));
    }
  }, [id, isEdit]);

  const set = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((p) => ({ ...p, [field]: type === 'checkbox' ? checked : (type === 'number' ? Number(value) : value) }));
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    const res: any = await vehicleService.createCategory(newCategoryName.trim());
    const cat = res.data?.data ?? res.data;
    setCategories((p) => [...p, cat]);
    setFormData((p) => ({ ...p, vehicleCategoryId: String(cat.id) }));
    setNewCategoryName('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.vehicleCategoryId) { setError('Select a vehicle category'); return; }
    setSaving(true);
    setError(null);
    const payload = {
      vehicleNumber: formData.vehicleNumber,
      registrationNumber: formData.registrationNumber,
      vin: formData.vin || undefined,
      make: formData.make,
      model: formData.model,
      year: formData.year,
      vehicleCategoryId: Number(formData.vehicleCategoryId),
      color: formData.color || undefined,
      seatingCapacity: formData.seatingCapacity,
      fuelType: formData.fuelType,
      transmission: formData.transmission,
      mileage: formData.mileage,
      rentalRate: formData.rentalRate,
      securityDeposit: formData.securityDeposit,
      gpsAvailable: formData.gpsAvailable,
      insuranceDetails: formData.insuranceDetails || undefined,
      isActive: formData.isActive,
    };
    try {
      if (isEdit && id) await vehicleService.update(Number(id), { ...payload, status: formData.status });
      else await vehicleService.create(payload);
      navigate('/car-rental/vehicles');
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Failed to save vehicle');
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div className="ims-create-body-page" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '60vh', display: 'flex' }}>
          <div className="spinner-border" role="status" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <form onSubmit={handleSubmit}>
        <div className="ims-create-body-page car-rental-page">
          <div className="ims-top-bar">
            <div className="ims-top-bar-left">
              <button type="button" className="ims-back-btn" onClick={() => navigate('/car-rental/vehicles')}>
                <ArrowLeft size={13} /> Vehicles
              </button>
              <div>
                <h1 className="ims-page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Car size={18} style={{ color: 'var(--ims-accent)' }} />
                  {isEdit ? 'Edit Vehicle' : 'New Vehicle'}
                </h1>
              </div>
            </div>
            <div className="ims-top-bar-right">
              <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/car-rental/vehicles')} disabled={saving}>Cancel</button>
              <button type="submit" className="ims-btn ims-btn-primary" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Update' : 'Save')}</button>
            </div>
          </div>

          {error && (
            <div className="ims-error-banner">
              <div className="ims-alert ims-alert-error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={15} /><span style={{ flex: 1 }}>{error}</span>
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            </div>
          )}

          <div className="ims-create-body">
            {/* ── Left panel: identity + specs ── */}
            <div className="ims-panel-left">
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title"><Car size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />Identity</span>
                </div>
                <div className="ims-card-body">
                  <div className="form-group">
                    <label className="form-label">Vehicle Number *</label>
                    <input className="form-input" value={formData.vehicleNumber} onChange={set('vehicleNumber')} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Registration Number *</label>
                    <input className="form-input" value={formData.registrationNumber} onChange={set('registrationNumber')} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">VIN</label>
                    <input className="form-input" value={formData.vin} onChange={set('vin')} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label className="form-label">Make *</label>
                      <input className="form-input" value={formData.make} onChange={set('make')} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Model *</label>
                      <input className="form-input" value={formData.model} onChange={set('model')} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Year</label>
                      <input className="form-input" type="number" value={formData.year} onChange={set('year')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Color</label>
                      <input className="form-input" value={formData.color} onChange={set('color')} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">Specifications</span>
                </div>
                <div className="ims-card-body">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label className="form-label">Seating Capacity</label>
                      <input className="form-input" type="number" value={formData.seatingCapacity} onChange={set('seatingCapacity')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Mileage (km)</label>
                      <input className="form-input" type="number" value={formData.mileage} onChange={set('mileage')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Fuel Type</label>
                      <select className="form-select" value={formData.fuelType} onChange={set('fuelType')}>
                        {['Petrol', 'Diesel', 'Electric', 'Hybrid', 'LPG'].map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Transmission</label>
                      <select className="form-select" value={formData.transmission} onChange={set('transmission')}>
                        {['Manual', 'Automatic'].map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Right panel: category, pricing, status ── */}
            <div className="ims-panel-right">
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">Category</span>
                </div>
                <div className="ims-card-body">
                  <div className="form-group">
                    <label className="form-label">Category *</label>
                    <select className="form-select" value={formData.vehicleCategoryId} onChange={set('vehicleCategoryId')} required>
                      <option value="">Select category</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Add New Category</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input className="form-input" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="e.g. SUV" />
                      <button type="button" className="ims-btn ims-btn-secondary" onClick={handleAddCategory}>Add</button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">Pricing</span>
                </div>
                <div className="ims-card-body">
                  <div className="form-group">
                    <label className="form-label">Rental Rate / Day *</label>
                    <input className="form-input" type="number" step="0.01" value={formData.rentalRate} onChange={set('rentalRate')} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Security Deposit</label>
                    <input className="form-input" type="number" step="0.01" value={formData.securityDeposit} onChange={set('securityDeposit')} />
                  </div>
                </div>
              </div>

              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">Status &amp; Options</span>
                </div>
                <div className="ims-card-body">
                  {isEdit && (
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select className="form-select" value={formData.status} onChange={set('status')}>
                        {['Available', 'Reserved', 'Rented', 'UnderMaintenance', 'Damaged', 'Retired'].map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Insurance Details</label>
                    <input className="form-input" value={formData.insuranceDetails} onChange={set('insuranceDetails')} />
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <input type="checkbox" checked={formData.gpsAvailable} onChange={set('gpsAvailable')} id="gps" />
                    <label htmlFor="gps" className="form-label" style={{ margin: 0 }}>GPS Available</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default VehicleCreatePage;
